import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  Fade,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  VideoCall as VideoCallIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Repeat as RepeatIcon,
  Lock as LockIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../supabaseClient';

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.grey[50], 0.5)})`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`,
  }
}));

// Profile Avatar Component
const ProfileAvatar = ({ userId, user, size = 28, sx = {} }) => {
  const [avatarUrl, setAvatarUrl] = useState('');
  
  useEffect(() => {
    if (!userId) return;
    
    const loadAvatar = async () => {
      try {
        const { data: files, error } = await supabase.storage
          .from('profile-avatars')
          .list(`${userId}/`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (error || !files || files.length === 0) return;

        const fileName = `${userId}/${files[0].name}`;
        const { data, error: signedUrlError } = await supabase.storage
          .from('profile-avatars')
          .createSignedUrl(fileName, 3600);
        
        if (!signedUrlError && data?.signedUrl) {
          setAvatarUrl(data.signedUrl);
        }
      } catch (err) {
        // Silent fail
      }
    };

    loadAvatar();
  }, [userId]);

  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <Avatar
      src={avatarUrl}
      sx={{ 
        width: size, 
        height: size, 
        fontSize: `${size * 0.4}px`,
        backgroundColor: 'grey.400',
        color: 'white',
        fontWeight: 500,
        ...sx
      }}
    >
      {getInitials()}
    </Avatar>
  );
};

const EventEditor = ({ 
  event, 
  onSave, 
  onDelete,
  onCancel, 
  users, 
  currentUser,
  company,
  mode = 'edit',
  timeFormat = '12hour',
  canEdit = true,
  canDelete = true
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: null,
    end_date: null,
    all_day: false,
    location: '',
    videocall_url: '',
    meeting_password: '',
    privacy: 'company',
    status: 'confirmed',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: null
  });
  
  const [attendees, setAttendees] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (event && mode === 'edit') {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        start_date: event.start_date ? dayjs(event.start_date) : null,
        end_date: event.end_date ? dayjs(event.end_date) : null,
        all_day: event.all_day || false,
        location: event.location || '',
        videocall_url: event.videocall_url || '',
        meeting_password: event.meeting_password || '',
        privacy: event.privacy || 'company',
        status: event.status || 'confirmed',
        is_recurring: event.is_recurring || false,
        recurrence_pattern: event.recurrence_pattern || 'weekly',
        recurrence_end_date: event.recurrence_end_date ? dayjs(event.recurrence_end_date) : null
      });
      
      // Load attendees if event exists
      if (event.id) {
        loadAttendees(event.id);
      }
    } else if (mode === 'create') {
      // Check if we have prefilled data from quick add "More Options"
      if (event && event.title) {
        // Use prefilled data
        setFormData({
          title: event.title || '',
          description: event.description || '',
          start_date: event.start_date ? dayjs(event.start_date) : dayjs(),
          end_date: event.end_date ? dayjs(event.end_date) : dayjs().add(1, 'hour'),
          all_day: event.all_day || false,
          location: event.location || '',
          videocall_url: event.videocall_url || '',
          meeting_password: event.meeting_password || '',
          privacy: event.privacy || 'company',
          status: event.status || 'confirmed',
          is_recurring: event.is_recurring || false,
          recurrence_pattern: event.recurrence_pattern || 'weekly',
          recurrence_end_date: event.recurrence_end_date ? dayjs(event.recurrence_end_date) : null
        });
        
        // Use prefilled attendees if they exist
        if (event.attendees && event.attendees.length > 0) {
          setAttendees(event.attendees);
        } else if (currentUser && currentUser.id) {
          // Default to current user as organizer
          setAttendees([{
            id: currentUser.id,
            user_id: currentUser.id,
            is_organizer: true,
            status: 'accepted',
            user: {
              id: currentUser.id,
              full_name: currentUser.full_name,
              email: currentUser.email
            }
          }]);
        } else {
          setAttendees([]);
        }
      } else {
        // Default create mode
        const defaultStart = event?.defaultStartDate ? dayjs(event.defaultStartDate) : dayjs();
        setFormData({
          title: '',
          description: '',
          start_date: defaultStart,
          end_date: defaultStart.add(1, 'hour'),
          all_day: false,
          location: '',
          videocall_url: '',
          meeting_password: '',
          privacy: 'company',
          status: 'confirmed',
          is_recurring: false,
          recurrence_pattern: 'weekly',
          recurrence_end_date: null
        });
        // Creator is automatically an attendee with robust validation
        if (currentUser && currentUser.id && typeof currentUser.id === 'string' && 
            currentUser.id !== 'undefined' && currentUser.id.length > 30) {
          const initialAttendee = {
            id: currentUser.id,
            user_id: currentUser.id,
            is_organizer: true,
            status: 'accepted',
            user: {
              id: currentUser.id,
              full_name: currentUser.full_name,
              email: currentUser.email
            }
          };
          setAttendees([initialAttendee]);
        } else {
          console.warn('=== INVALID CURRENT USER ID, EMPTY ATTENDEES ===', currentUser);
          setAttendees([]);
        }
      }
    }
  }, [event, mode]);

  const loadAttendees = async (eventId) => {
    try {
      const { data: attendeesData, error } = await supabase
        .from('event_attendees')
        .select('*')
        .eq('event_id', eventId);

      if (error) throw error;

      // Get user details for attendees
      if (attendeesData && attendeesData.length > 0) {
        const userIds = attendeesData.map(a => a.user_id);
        
        const { data: usersData, error: usersError } = await supabase
          .from('profiles')
          .select('id, full_name, first_name, last_name')
          .in('id', userIds);

        if (usersError) throw usersError;

        // Get emails
        const { data: emailsData } = await supabase.rpc('get_user_emails_for_company', {
          p_company_id: company.id
        });

        // Combine data
        const attendeesWithDetails = attendeesData.map(attendee => {
          const userProfile = usersData?.find(u => u.id === attendee.user_id);
          const userEmail = emailsData?.find(e => e.user_id === attendee.user_id);
          return {
            ...attendee,
            user: {
              id: attendee.user_id,
              full_name: userProfile?.full_name || userProfile?.first_name || userProfile?.last_name,
              email: userEmail?.email
            }
          };
        });

        setAttendees(attendeesWithDetails);
      } else {
        setAttendees([]);
      }
    } catch (error) {
      console.error('Error loading attendees:', error);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'all_day') {
      if (value) {
        // When turning on all_day, set times to start/end of day
        setFormData(prev => ({
          ...prev,
          [field]: value,
          start_date: prev.start_date ? prev.start_date.startOf('day') : null,
          end_date: prev.end_date ? prev.end_date.endOf('day') : null
        }));
      } else {
        // When turning off all_day, keep the dates but clear times (user will set them)
        setFormData(prev => ({
          ...prev,
          [field]: value,
          start_date: prev.start_date ? prev.start_date.startOf('day') : null,
          end_date: prev.end_date ? prev.end_date.startOf('day') : null
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      setSnackbarMessage('You do not have permission to edit calendar events');
      setSnackbarOpen(true);
      return;
    }
    
    if (!formData.title.trim()) {
      alert('Please enter an event title');
      return;
    }
    
    // Validate date range
    if (formData.start_date && formData.end_date) {
      const startTime = new Date(formData.start_date);
      const endTime = new Date(formData.end_date);
      
      if (endTime <= startTime) {
        alert('Invalid date range: End date must be after start date');
        return;
      }
    }
    
    try {
      // Robust validation for attendees - prevent undefined UUIDs
      const validAttendees = attendees.filter(attendee => {
        if (!attendee || !attendee.user_id || typeof attendee.user_id !== 'string' || 
            attendee.user_id === 'undefined' || attendee.user_id.length < 30) {
          console.warn('Filtering out invalid attendee in EventEditor:', attendee);
          return false;
        }
        return true;
      });
      
      // Validate that we have proper UUID values for company and user
      if (!company?.id || company.id === 'undefined' || typeof company.id !== 'string' || company.id.length < 30) {
        throw new Error(`Invalid company ID in EventEditor: ${company?.id}`);
      }
      if (!currentUser?.id || currentUser.id === 'undefined' || typeof currentUser.id !== 'string' || currentUser.id.length < 30) {
        throw new Error(`Invalid current user ID in EventEditor: ${currentUser?.id}`);
      }
      
      const eventData = {
        title: formData.title.trim(),
        description: formData.description || '',
        start_date: formData.start_date ? formData.start_date.toISOString() : null,
        end_date: formData.end_date ? formData.end_date.toISOString() : null,
        all_day: formData.all_day,
        location: formData.location || null,
        videocall_url: formData.videocall_url || null,
        meeting_password: formData.meeting_password || null,
        privacy: formData.privacy,
        status: formData.status,
        is_recurring: formData.is_recurring,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        recurrence_end_date: formData.is_recurring && formData.recurrence_end_date ? formData.recurrence_end_date.toISOString() : null,
        id: event?.id,
        attendees: validAttendees
      };
      
      if (onSave) {
        await onSave(eventData);
        setSnackbarMessage('Event saved successfully!');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('=== ERROR IN EVENT EDITOR ===');
      console.error('Error saving event:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setSnackbarMessage(`Failed to save event: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleAddAttendee = (userId) => { 
    const user = users.find(u => u.id === userId);

    // Robust validation for userId - prevent undefined UUIDs
    if (user && userId && typeof userId === 'string' && userId !== 'undefined' && 
        userId.length > 30 && !attendees.find(a => a.user_id === userId)) {
      const newAttendee = {
        id: userId,
        user_id: userId,
        is_organizer: false,
        status: 'pending',
        user: {
          id: userId,
          full_name: user.full_name,
          email: user.email
        }
      };
      setAttendees(prev => [...prev, newAttendee]);
    } else {
      console.warn('Invalid userId or user not found in EventEditor:', userId, user);
    }
  };

  const handleRemoveAttendee = (userId) => {
    // Don't allow removing the organizer
    if (attendees.find(a => a.user_id === userId && a.is_organizer)) {
      return;
    }
    setAttendees(prev => prev.filter(a => a.user_id !== userId));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box sx={{ 
          minHeight: '100vh', 
          backgroundColor: alpha(theme.palette.grey[50], 0.5),
          pb: 4
        }}>
          <Container maxWidth="xl" sx={{ py: 4, px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100vw !important' }}>
            <Grid container spacing={2}>
              {/* Top Row - Title (Full Width) */}
              <Grid item xs={12}>
                <SectionCard sx={{ borderRadius: 3, mb: 1 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                          p: 1, 
                          borderRadius: 2, 
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }}>
                          <EventIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                          Event Title
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={onCancel}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            px: 3,
                            py: 1.5,
                            fontSize: '0.95rem',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.grey[100], 0.8),
                              transform: 'translateY(-1px)'
                            }
                          }}
                        >
                          <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
                          Back to Calendar
                        </Button>
                        
                        {mode === 'edit' && event?.id && canDelete && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setDeleteDialogOpen(true)}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 500,
                              px: 3,
                              py: 1.5,
                              fontSize: '0.95rem',
                              border: `1px solid ${alpha(theme.palette.error.main, 0.8)}`,
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                transform: 'translateY(-1px)'
                              }
                            }}
                          >
                            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
                            Delete Event
                          </Button>
                        )}
                        
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          disabled={!canEdit || !formData.title.trim() || 
                            (formData.start_date && formData.end_date && 
                             new Date(formData.end_date) <= new Date(formData.start_date))}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            px: 3,
                            py: 1.5,
                            fontSize: '0.95rem',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            '&:disabled': {
                              opacity: 0.6,
                              boxShadow: 'none',
                              transform: 'none'
                            }
                          }}
                        >
                          <SaveIcon sx={{ mr: 1, fontSize: 20 }} />
                          Save Event
                        </Button>
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Enter event title..."
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      InputProps={{
                        sx: {
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          borderRadius: 3,
                          mt: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: alpha(theme.palette.grey[400], 0.8)
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px',
                            borderColor: theme.palette.primary.main
                          }
                        }
                      }}
                    />
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* Second Row - Event Details & Settings */}
              <Grid item xs={12} lg={6}>
                <SectionCard sx={{ borderRadius: 3, mb: 1, minHeight: '420px', height: '420px' }}>
                  <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Event Details
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.all_day}
                          onChange={(e) => handleInputChange('all_day', e.target.checked)}
                          disabled={!canEdit}
                        />
                      }
                      label="All Day"
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <DateTimePicker
                        label={formData.all_day ? "Start Date" : "Start Date & Time"}
                        value={formData.start_date}
                        onChange={(date) => {
                          handleInputChange('start_date', date);
                          if (date) {
                            // Always set end date to 1 hour after start date
                            const newEndDate = formData.all_day ? date : date.add(1, 'hour');
                            handleInputChange('end_date', newEndDate);
                          }
                        }}
                        format={formData.all_day ? 'MM/DD/YYYY' : (timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A')}
                        ampm={timeFormat !== '24hour'}
                        views={formData.all_day ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hours', 'minutes']}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              },
                              '& fieldset': {
                                borderWidth: '1px !important'
                              }
                            }
                          },
                          popper: {
                            sx: {
                              zIndex: 99999
                            }
                          }
                        }}
                      />
                      
                      <DateTimePicker
                        label={formData.all_day ? "End Date" : "End Date & Time"}
                        value={formData.end_date}
                        onChange={(date) => handleInputChange('end_date', date)}
                        format={formData.all_day ? 'MM/DD/YYYY' : (timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A')}
                        ampm={timeFormat !== '24hour'}
                        views={formData.all_day ? ['year', 'month', 'day'] : ['year', 'month', 'day', 'hours', 'minutes']}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              },
                              '& fieldset': {
                                borderWidth: '1px !important'
                              }
                            }
                          },
                          popper: {
                            sx: {
                              zIndex: 99999
                            }
                          }
                        }}
                      />
                    </Box>

                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      disabled={!canEdit}
                      size="small"
                      sx={{ 
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                      InputProps={{
                        startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Video Call URL"
                        value={formData.videocall_url}
                        onChange={(e) => handleInputChange('videocall_url', e.target.value)}
                        disabled={!canEdit}
                        size="small"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                        InputProps={{
                          startAdornment: <VideoCallIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Meeting Password"
                        value={formData.meeting_password}
                        onChange={(e) => handleInputChange('meeting_password', e.target.value)}
                        disabled={!canEdit}
                        size="small"
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        }}
                      />
                    </Box>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        disabled={!canEdit}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        MenuProps={{
                          disableScrollLock: true,
                          disablePortal: false,
                          disableAutoFocus: true,
                          disableEnforceFocus: true,
                          PaperProps: {
                            sx: {
                              position: 'absolute !important',
                              minWidth: 140,
                              contain: 'layout style paint',
                              zIndex: 9999,
                              borderRadius: 3,
                              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                            }
                          },
                          sx: {
                            zIndex: 9999
                          }
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="tentative">Tentative</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* Second Row - Attendees */}
              <Grid item xs={12} lg={6}>
                <SectionCard sx={{ borderRadius: 3, mb: 1, minHeight: '420px', height: '420px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Attendees
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Add Attendees</InputLabel>
                      <Select
                        multiple
                        value={attendees.map(a => a.user_id)}
                        label="Add Attendees"
                        disabled={!canEdit}
                        onChange={(e) => {
                          const selectedIds = e.target.value;
                          // Add new attendees
                          selectedIds.forEach(id => {
                            if (!attendees.find(a => a.user_id === id)) {
                              handleAddAttendee(id);
                            }
                          });
                          // Remove unselected attendees (except organizer)
                          attendees.forEach(attendee => {
                            if (!selectedIds.includes(attendee.user_id) && !attendee.is_organizer) {
                              handleRemoveAttendee(attendee.user_id);
                            }
                          });
                        }}
                        MenuProps={{
                          disableScrollLock: true,
                          disablePortal: false,
                          disableAutoFocus: true,
                          disableEnforceFocus: true,
                          PaperProps: {
                            sx: {
                              position: 'absolute !important',
                              minWidth: 300,
                              contain: 'layout style paint',
                              zIndex: 9999,
                              borderRadius: 3,
                              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                            }
                          },
                          sx: {
                            zIndex: 9999
                          }
                        }}
                        sx={{ borderRadius: 2 }}
                        renderValue={() => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {attendees.map((attendee) => (
                              <Chip
                                key={attendee.user_id}
                                label={`${attendee.user?.full_name || attendee.user?.email || 'Unknown'}${attendee.is_organizer ? ' (Organizer)' : ''}`}
                                size="small"
                                avatar={<ProfileAvatar
                                  userId={attendee.user_id}
                                  user={attendee.user}
                                  size={20}
                                  sx={{ 
                                    width: '20px !important', 
                                    height: '20px !important', 
                                    backgroundColor: '#9e9e9e !important', 
                                    fontSize: '0.75rem !important', 
                                    color: '#ffffff !important', 
                                    border: '1px solid #ccc'
                                  }}
                                />}
                                onDelete={!attendee.is_organizer ? () => handleRemoveAttendee(attendee.user_id) : undefined}
                                sx={{ 
                                  borderRadius: 2, 
                                  fontSize: '0.8rem',
                                  backgroundColor: attendee.is_organizer ? alpha(theme.palette.primary.main, 0.1) : 'default'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {users.filter(user => user && user.id).map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ProfileAvatar
                                userId={user.id}
                                user={user}
                                size={20}
                                sx={{ bgcolor: 'grey.400', fontSize: '0.75rem', color: 'white' }}
                              />
                              <Typography sx={{ fontSize: '0.9rem' }}>
                                {user.full_name || user.email}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Display attendees with their status */}
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {attendees.map((attendee) => (
                        <Box key={attendee.user_id} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 1,
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                          borderRadius: 2,
                          mb: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ProfileAvatar
                              userId={attendee.user_id}
                              user={attendee.user}
                              size={24}
                              sx={{ bgcolor: 'grey.400', fontSize: '0.75rem', color: 'white' }}
                            />
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                {attendee.user?.full_name || attendee.user?.email || 'Unknown'}
                                {attendee.is_organizer && ' (Organizer)'}
                              </Typography>
                            </Box>
                          </Box>
                          {!attendee.is_organizer && canEdit && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveAttendee(attendee.user_id)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <SectionCard sx={{ borderRadius: 3, minHeight: '300px' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      Description
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={20}
                      placeholder="Enter event description..."
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={!canEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: alpha(theme.palette.grey[50], 0.5)
                        }
                      }}
                    />
                  </CardContent>
                </SectionCard>
              </Grid>

              {/* More Options */}
              <Grid item xs={12}>
                <SectionCard sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: 'primary.main', fontSize: '1rem' }}>
                      More Options
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.is_recurring}
                              onChange={(e) => handleInputChange('is_recurring', e.target.checked)}
                              disabled={!canEdit}
                            />
                          }
                          label="Recurring Event"
                          sx={{ mb: 2 }}
                        />
                      </Grid>

                      {formData.is_recurring && (
                        <>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Pattern</InputLabel>
                              <Select
                                value={formData.recurrence_pattern}
                                label="Pattern"
                                disabled={!canEdit}
                                onChange={(e) => handleInputChange('recurrence_pattern', e.target.value)}
                                MenuProps={{
                                  disableScrollLock: true,
                                  disablePortal: false,
                                  disableAutoFocus: true,
                                  disableEnforceFocus: true,
                                  PaperProps: {
                                    sx: {
                                      position: 'absolute !important',
                                      minWidth: 140,
                                      contain: 'layout style paint',
                                      zIndex: 9999,
                                      borderRadius: 3,
                                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                                    }
                                  },
                                  sx: {
                                    zIndex: 9999
                                  }
                                }}
                                sx={{ 
                                  borderRadius: 2,
                                  height: '40px !important',
                                  '& .MuiOutlinedInput-root': {
                                    height: '40px !important'
                                  },
                                  '& .MuiSelect-select': {
                                    height: '40px !important',
                                    lineHeight: '40px !important',
                                    padding: '0 14px !important',
                                    display: 'flex !important',
                                    alignItems: 'center !important',
                                    boxSizing: 'border-box !important'
                                  }
                                }}
                              >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <DateTimePicker
                              label="Repeat Until"
                              value={formData.recurrence_end_date}
                              onChange={(date) => handleInputChange('recurrence_end_date', date)}
                              disabled={!canEdit}
                              format="MM/DD/YYYY"
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  sx: {
                                    '& .MuiOutlinedInput-root': { 
                                      borderRadius: 2,
                                      height: '40px !important'
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      height: '40px !important',
                                      lineHeight: '40px !important',
                                      padding: '0 14px !important',
                                      boxSizing: 'border-box !important'
                                    }
                                  }
                                },
                                popper: {
                                  sx: {
                                    zIndex: 99999
                                  }
                                }
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </SectionCard>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Fade>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        BackdropComponent={Backdrop}
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: alpha(theme.palette.grey[900], 0.5) }
        }}
        PaperProps={{
          sx: {
            position: 'absolute !important',
            borderRadius: 3,
            maxWidth: 480,
            mx: 'auto',
            contain: 'layout style paint',
            zIndex: 9999
          }
        }}
        sx={{
          zIndex: 9999
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500, 
          color: 'error.main',
          py: 3,
          fontSize: '1.25rem',
          textAlign: 'center'
        }}>
          Delete Event
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3, px: 4, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
            Are you sure you want to delete this event?
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
            "{event?.title}"
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 2, gap: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            variant="outlined"
            sx={{ 
              borderRadius: 3, 
              fontWeight: 500,
              px: 4,
              py: 1.5,
              minWidth: 120,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              onDelete(event.id);
              setDeleteDialogOpen(false);
            }} 
            variant="contained"
            color="error"
            disabled={!canDelete}
            sx={{ 
              borderRadius: 3, 
              fontWeight: 500,
              px: 4,
              py: 1.5,
              minWidth: 120,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.error.main, 0.4)}`
              },
              '&:disabled': {
                opacity: 0.6,
                boxShadow: 'none'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default EventEditor; 